/**
 * FormFieldsParser.ts
 * Simple parser to extract fields from new API response format and format submission payload
 */

export class FormFieldsParser {
  
  /**
   * Extract fields from API response and return in meta.formFields format
   * Works with both old and new API response formats
   */
  static extractFields(response: any): any[] {
    // New format: has sections array with fields
    if (response?.sections && Array.isArray(response.sections)) {
      const allFields: any[] = [];
      
      // Extract all fields from all sections
      response.sections.forEach((section: any) => {
        if (section.fields && Array.isArray(section.fields)) {
          allFields.push(...section.fields);
        }
      });
      
      return allFields;
    }
    
    // Current ApiResponse.json format: sections object with nested fields
    if (response?.sections && typeof response.sections === 'object' && !Array.isArray(response.sections)) {
      const allFields: any[] = [];
      
      // Loop through each section in the object
      Object.values(response.sections).forEach((section: any) => {
        if (section.fields && Array.isArray(section.fields)) {
          allFields.push(...section.fields);
        }
      });
      
      return allFields;
    }
    
    // Existing format: already has formFields  
     return Array.isArray(response?.formFields) ? response.formFields : [];
  }

  /**
   * Format form submission data according to API's expected payload format
   * @param originalApiResponse - The original API response that contains form metadata
   * @param formValues - The form data values submitted by user
   * @param isFinalSubmission - Whether this is the final submission (default: false for development)
   * @returns Formatted payload for API submission
   */
  static formatSubmissionPayload(
    originalApiResponse: any,
    formValues: Record<string, any>,
    isFinalSubmission: boolean = false
  ): any {
    // Extract formKey from original API response
    const formKey = originalApiResponse?.formKey || 'unknown.form';
    
    // For now, use the first section ID as default (since we flatten all fields)
    // In future, you might want to track which section each field belongs to
    let sectionId = 'intake'; // Default section
    
    if (originalApiResponse?.sections && Array.isArray(originalApiResponse.sections)) {
      // Safely check if array has elements before accessing first element's ID
      if (originalApiResponse.sections.length > 0 && originalApiResponse.sections[0]?.id) {
        sectionId = originalApiResponse.sections[0].id;
      }
    } else if (originalApiResponse?.sections && typeof originalApiResponse.sections === 'object') {
      // Use the first section key from object format
      const sectionKeys = Object.keys(originalApiResponse.sections);
      sectionId = sectionKeys[0] || sectionId;
    }

    // Create the API expected payload format
    const submissionPayload = {
      formKey: formKey,
      sectionId: sectionId,
      formData: formValues,
      isFinalSubmission: isFinalSubmission
    };    
    
    return submissionPayload;
  }

  /**
   * Helper method to determine which section a field belongs to
   * @param fieldId - The field ID to look for
   * @param originalApiResponse - The original API response
   * @returns The section ID that contains this field, or 'intake' as default
   */
  static getFieldSectionId(fieldId: string, originalApiResponse: any): string {
    if (originalApiResponse?.sections) {      
      if (Array.isArray(originalApiResponse.sections)) {
        for (const section of originalApiResponse.sections) {
          if (section && section.fields && Array.isArray(section.fields)) {
            const hasField = section.fields.some((field: any) => field.id === fieldId);
            if (hasField) {
              return section.id;
            }
          }
        }
      }else if (typeof originalApiResponse.sections === 'object') {
        try {
          for (const section of Object.values(originalApiResponse.sections)) {
            if (section && typeof section === 'object' && (section as any).fields && Array.isArray((section as any).fields)) {
              const hasField = (section as any).fields.some((field: any) => field.id === fieldId);
              if (hasField) {
                return (section as any).id;
              }
            }
          }
        } catch (error) {
          // Continue to fallback
        }
      }
    }
    
    // Default fallback (unchanged)
    return 'intake';
  }

  /**
   * Test/Demo method to show expected payload format
   * @param originalApiResponse - The API response from ApiResponse.json
   * @returns Example of formatted submission payload
   */
  static getExampleSubmissionPayload(originalApiResponse: any): any {
    const exampleFormData = {
      "ShortDescription": "Need CSDR deck for Q1 business review",
      "description": "We need to prepare a CSDR presentation deck for our quarterly business review meeting with the customer. The meeting is scheduled for next month and we need comprehensive analytics data.",
      "opencode": "Other",
      "customerName": "Contoso Ltd",
      "agreementId": "AGR-2024-001"
    };

    return this.formatSubmissionPayload(originalApiResponse, exampleFormData, false);
  }
}